/**
 * Patches the Supabase client so that mutations performed while offline
 * are transparently queued in IndexedDB and replayed when connection returns.
 *
 * Reads are unaffected (they keep going through the network or fail naturally,
 * and our DataContext provides cached fallbacks).
 */
import { supabase } from '@/integrations/supabase/client';
import { queueAdd } from './offline-db';
import { isOnline } from './offline-sync';

// Tables we DO NOT queue (sensitive / require server-side validation)
const NEVER_QUEUE = new Set([
  'audit_logs',
  'user_permissions',
  'user_cancel_passwords',
  'company_settings',
  'invoices',
  'esocial_events',
]);

let patched = false;

export function installOfflinePatch() {
  if (patched) return;
  patched = true;

  const originalFrom = supabase.from.bind(supabase);

  (supabase as any).from = (table: string) => {
    const builder: any = originalFrom(table as any);
    if (NEVER_QUEUE.has(table)) return builder;

    // insert
    const origInsert = builder.insert?.bind(builder);
    if (origInsert) {
      builder.insert = (values: any, opts?: any) => {
        if (isOnline()) return origInsert(values, opts);
        const rows = Array.isArray(values) ? values : [values];
        return makeQueuedThenable(async () => {
          for (const r of rows) await queueAdd({ table, op: 'insert', payload: r });
          // Return shape similar to PostgrestSingleResponse
          return { data: rows, error: null };
        });
      };
    }

    // update
    const origUpdate = builder.update?.bind(builder);
    if (origUpdate) {
      builder.update = (values: any, opts?: any) => {
        if (isOnline()) return origUpdate(values, opts);
        const filterBuilder = origUpdate(values, opts);
        return wrapFilterBuilder(filterBuilder, async (match) => {
          await queueAdd({ table, op: 'update', match, payload: values });
          return { data: null, error: null };
        });
      };
    }

    // delete
    const origDelete = builder.delete?.bind(builder);
    if (origDelete) {
      builder.delete = (opts?: any) => {
        if (isOnline()) return origDelete(opts);
        const filterBuilder = origDelete(opts);
        return wrapFilterBuilder(filterBuilder, async (match) => {
          await queueAdd({ table, op: 'delete', match });
          return { data: null, error: null };
        });
      };
    }

    return builder;
  };
}

/** Builds a thenable that resolves to a Supabase-like response shape. */
function makeQueuedThenable(fn: () => Promise<any>) {
  const t: any = {};
  t.select = () => makeQueuedThenable(async () => {
    const r = await fn();
    return { ...r, data: r.data?.[0] ?? null };
  });
  t.single = () => t;
  t.maybeSingle = () => t;
  t.then = (resolve: any, reject: any) => fn().then(resolve, reject);
  return t;
}

/**
 * Captures filters (.eq) applied to update/delete builders and turns the
 * final await into a queued operation when offline.
 */
function wrapFilterBuilder(builder: any, runWhenAwaited: (match: Record<string, any>) => Promise<any>) {
  const match: Record<string, any> = {};
  const wrapper: any = {};
  const proxy = new Proxy(wrapper, {
    get(_t, prop: string) {
      if (prop === 'eq') {
        return (col: string, val: any) => { match[col] = val; return proxy; };
      }
      if (prop === 'then') {
        return (resolve: any, reject: any) => runWhenAwaited(match).then(resolve, reject);
      }
      if (prop === 'select' || prop === 'single' || prop === 'maybeSingle') {
        return () => proxy;
      }
      // Fallback: forward filter calls (in/.gt/etc) — we just stash unknown filters as match-all.
      const orig = builder[prop];
      if (typeof orig === 'function') {
        return (...args: any[]) => { try { orig.apply(builder, args); } catch {} return proxy; };
      }
      return undefined;
    },
  });
  return proxy;
}